import React, { useState, useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogClose, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, CalendarIcon, AlertTriangle, CheckCircle, X, MapPinIcon, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getStoredUTMParams, trackPostHogEvent } from "@/lib/analytics";
import posthog from 'posthog-js';
import { v4 as uuidv4 } from "uuid";
import ConfirmCloseModal from "./ConfirmCloseModal";
import Step8AiSuggestions from "./Step8AiSuggestions";
import EmailOnlyMissingModal from "./EmailOnlyMissingModal";
import { 
  trackFormOpened, 
  trackFormStepViewed, 
  trackFormStepAbandoned, 
  trackFormSubmitted 
} from "@/lib/posthog";
import { sanitizeFormData } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";

// ============= TYPES =============
export type EventType = "corporate_retreat" | "away_day" | "corporate_party" | "team_building" | "conference" | "product_launch" | "other";
export type BudgetRange = "10k_or_less" | "10k_50k" | "50k_100k" | "100k_plus";
export type LocationType = "beach" | "mountain" | "urban";
export interface EventDetailsFormData {
  eventType: string;
  startDate: string;
  endDate: string;
  participantsCount: string;
  budget: string;
  location: string;
  locationType?: LocationType;
  month?: string;
}
export interface ContactInfoFormData {
  workEmail: string;
  phoneNumber: string;
}
export interface LeadFormData {
  eventDetails: EventDetailsFormData;
  contactInfo: ContactInfoFormData;
}
export interface FormStepProps {
  onNext: (data: any) => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  initialData: any;
  canSkip?: boolean;
  isSubmitting?: boolean;
  showBackButton?: boolean;
}
export interface FormLayoutProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  image?: string;
  imageCaption?: string;
  split?: boolean | "left-image";
}
export interface StepContainerProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  image?: string;
  imageCaption?: string;
  split?: boolean | "left-image";
  onNext?: (data: any) => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  initialData?: any;
  canSkip?: boolean;
  isSubmitting?: boolean;
  showBackButton?: boolean;
  currentStep?: number;
  totalSteps?: number;
  hideStepIndicator?: boolean;
}

// ============= HOOK useLeadForm =============
const initialEventDetails: EventDetailsFormData = {
  eventType: "",
  startDate: "",
  endDate: "",
  participantsCount: "",
  budget: "",
  location: "",
  locationType: undefined,
  month: undefined
};
const initialContactInfo: ContactInfoFormData = {
  workEmail: "",
  phoneNumber: ""
};
const formatBudget = (budgetRange: BudgetRange | string): string => {
  switch (budgetRange) {
    case "10k_or_less":
      return "$10K or less";
    case "10k_50k":
      return "$10K-$50K";
    case "50k_100k":
      return "$50K-$100K";
    case "100k_plus":
      return "$100K+";
    default:
      return budgetRange as string;
  }
};
const formatEventType = (eventType: EventType | string): string => {
  switch (eventType) {
    case "corporate_retreat":
      return "Corporate Retreat";
    case "away_day":
      return "Away Day";
    case "corporate_party":
      return "Corporate Party";
    case "team_building":
      return "Team Building";
    case "conference":
      return "Conference";
    case "product_launch":
      return "Product Launch";
    case "other":
      return "Other";
    default:
      return eventType as string;
  }
};
function useLeadForm(initialFormData?: {
  eventDetails: EventDetailsFormData;
  contactInfo: ContactInfoFormData;
  startAtEmailStep?: boolean;
}) {
  const [currentStep, setCurrentStep] = useState<number>(initialFormData?.startAtEmailStep ? 6 : 1);
  const [eventDetails, setEventDetails] = useState<EventDetailsFormData>(initialFormData?.eventDetails || initialEventDetails);
  const [contactInfo, setContactInfo] = useState<ContactInfoFormData>(initialFormData?.contactInfo || initialContactInfo);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiPlannerLoading, setAiPlannerLoading] = useState(false);
  const [aiPlannerSessionUrl, setAiPlannerSessionUrl] = useState<string | null>(null);
  const [aiPlannerError, setAiPlannerError] = useState(false);
  const aiPlannerPromiseRef = useRef<Promise<any> | null>(null);
  const { startTracking, getElapsedSeconds, resetTracking } = useFormTracking();
  const resetForm = () => {
    setCurrentStep(1);
    setEventDetails(initialEventDetails);
    setContactInfo(initialContactInfo);
    setAiPlannerLoading(false);
    setAiPlannerSessionUrl(null);
    setAiPlannerError(false);
    aiPlannerPromiseRef.current = null;
    resetTracking();
  };

  const parseBudgetNumeric = (budgetString: string): number => {
    if (!budgetString) return Infinity;
    const cleaned = budgetString.replace(/[^\d]/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? Infinity : num;
  };

  const isEligibleForAiPlanner = (budget: string, eventType: string): boolean => {
    const numericBudget = parseBudgetNumeric(budget);
    return numericBudget < 6000 && eventType !== "team_building";
  };

  const startAiPlannerPreFetch = (currentEventDetails: EventDetailsFormData) => {
    setAiPlannerLoading(true);
    setAiPlannerSessionUrl(null);
    setAiPlannerError(false);

    const eventTypeLabel = formatEventType(currentEventDetails.eventType);
    let dateInfo = "";
    if (currentEventDetails.month) {
      dateInfo = `en ${currentEventDetails.month}`;
    } else if (currentEventDetails.startDate && currentEventDetails.endDate) {
      dateInfo = `du ${currentEventDetails.startDate} au ${currentEventDetails.endDate}`;
    } else if (currentEventDetails.startDate) {
      dateInfo = `le ${currentEventDetails.startDate}`;
    }

    const brief = `${eventTypeLabel} pour ${currentEventDetails.participantsCount} personnes${currentEventDetails.location ? ` à ${currentEventDetails.location}` : ""}${dateInfo ? `, ${dateInfo}` : ""}, budget ${currentEventDetails.budget}`;

    const promise = supabase.functions.invoke('call-ai-planner', {
      body: { brief, locale: "fr" }
    }).then(({ data, error }) => {
      if (error || !data?.sessionUrl) {
        console.error("AI Planner error:", error, data);
        setAiPlannerError(true);
      } else {
        setAiPlannerSessionUrl(data.sessionUrl);
      }
      setAiPlannerLoading(false);
    }).catch((err) => {
      console.error("AI Planner fetch failed:", err);
      setAiPlannerError(true);
      setAiPlannerLoading(false);
    });

    aiPlannerPromiseRef.current = promise;
  };
  const isOnlyEmailMissing = () => {
    // Vérifier si toutes les étapes essentielles sont remplies sauf l'email
    const isEventTypeCompleted = eventDetails.eventType.trim() !== "";
    const isDateCompleted = eventDetails.startDate.trim() !== "" || eventDetails.month?.trim() !== "";
    const isParticipantsCompleted = eventDetails.participantsCount.trim() !== "";
    const isBudgetCompleted = eventDetails.budget.trim() !== "";

    // L'utilisateur peut avoir atteint l'étape 6 (contact) mais sans email
    const isEmailMissing = contactInfo.workEmail.trim() === "";
    console.log('isOnlyEmailMissing check:', {
      isEventTypeCompleted,
      isDateCompleted,
      isParticipantsCompleted,
      isBudgetCompleted,
      isEmailMissing,
      currentStep,
      eventDetails,
      contactInfo
    });
    return isEventTypeCompleted && isDateCompleted && isParticipantsCompleted && isBudgetCompleted && isEmailMissing;
  };
  const handleStepSubmit = (stepData: any, step: number) => {
    if (step === 1) {
      setEventDetails(prev => ({
        ...prev,
        eventType: stepData.eventType
      }));
    } else if (step === 2) {
      setEventDetails(prev => ({
        ...prev,
        startDate: stepData.startDate,
        endDate: stepData.endDate,
        month: stepData.month
      }));
    } else if (step === 3) {
      setEventDetails(prev => ({
        ...prev,
        location: stepData.location,
        locationType: stepData.locationType
      }));
    } else if (step === 4) {
      setEventDetails(prev => ({
        ...prev,
        participantsCount: stepData.participantsCount
      }));
    } else if (step === 5) {
      const updatedDetails = {
        ...eventDetails,
        budget: stepData.budget
      };
      setEventDetails(prev => ({
        ...prev,
        budget: stepData.budget
      }));
      // Pre-fetch AI Planner if eligible
      if (isEligibleForAiPlanner(stepData.budget, eventDetails.eventType)) {
        startAiPlannerPreFetch(updatedDetails);
      }
    } else if (step === 6) {
      return handleSubmit(stepData);
    }
    
    const nextStep = step + 1;
    setCurrentStep(nextStep);
    
    // Track step viewed
    const stepNames = ["Event Type", "Date Selection", "Location", "Participants", "Budget", "Contact Info", "Success"];
    trackFormStepViewed(nextStep, stepNames[nextStep - 1] || "Unknown", nextStep === 3 || nextStep === 5 || nextStep === 6);
  };
  const handleSkipStep = (step: number) => {
    if (step === 3) {
      setEventDetails(prev => ({
        ...prev,
        location: "",
        locationType: undefined
      }));
    } else if (step === 5) {
      setEventDetails(prev => ({
        ...prev,
        budget: ""
      }));
    } else if (step === 6) {
      setContactInfo(prev => ({
        ...prev,
        workEmail: "",
        phoneNumber: ""
      }));
    }
    setCurrentStep(step + 1);
  };
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSubmit = async (contactData: ContactInfoFormData) => {
    setIsSubmitting(true);
    setContactInfo(contactData);
    try {
      console.log("Form data to submit:", {
        eventDetails,
        contactInfo: contactData
      });
      const budgetFormatted = formatBudget(eventDetails.budget);
      let startDateToSave = eventDetails.startDate;
      let endDateToSave = eventDetails.endDate;
      const isMonthSelection = eventDetails.month && eventDetails.month.trim() !== "" && (!eventDetails.startDate || eventDetails.startDate.trim() === "");
      if (isMonthSelection) {
        startDateToSave = eventDetails.month;
        endDateToSave = eventDetails.month;
      }
      const leadData = {
        id: uuidv4(),
        eventtype: eventDetails.eventType,
        startdate: startDateToSave,
        enddate: endDateToSave,
        participantscount: eventDetails.participantsCount,
        budget: budgetFormatted,
        location: eventDetails.location,
        workemail: contactData.workEmail,
        created_at: new Date().toISOString()
      };
      console.log("Formatted data to submit:", leadData);
      const {
        data,
        error
      } = await supabase.from('alert-form-fr').insert(leadData);
      const eventTypeDisplay = formatEventType(eventDetails.eventType);
      let eventTypeText = eventTypeDisplay;
      let dateDisplay = "";
      if (isMonthSelection) {
        dateDisplay = `${eventDetails.month}`;
      } else if (eventDetails.startDate && eventDetails.endDate) {
        dateDisplay = `du ${eventDetails.startDate} au ${eventDetails.endDate}`;
      } else if (eventDetails.startDate) {
        dateDisplay = `le ${eventDetails.startDate}`;
      } else {
        dateDisplay = "Dates non spécifiées";
      }
      
      // Capture UTM SDR
      const storedUTMParams = getStoredUTMParams();
      const urlParams = new URLSearchParams(window.location.search);
      const sdr = urlParams.get('utm_sdr') || storedUTMParams.utm_sdr || '—';

      const slackMessage = `*Nouvelle demande*\n*Canal d'acquisition* : Formulaire\n*Market* : FR 🇫🇷\n*SDR* : ${sdr}\n*Source du lead* : Landing Lovable\n\n*E-mail pro* : ${contactData.workEmail}\n*Type d'event* : ${eventTypeText}\n*Capacité* : ${eventDetails.participantsCount} personnes\n*Dates* : ${dateDisplay}\n*Location* : ${eventDetails.location}\n*Budget* : ${budgetFormatted}`;
      try {
        await sendSlackMessage(slackMessage, "C0B0LPU0SCS");
        
        // Track brief completion only after successful Slack delivery
        posthog.capture('brief_modal_completed');
      } catch (error) {
        console.error("Form submission error details:", error);
        throw error;
      }
      
      // Track form submitted
      const completionTime = getElapsedSeconds();
      trackFormSubmitted(
        formatEventType(eventDetails.eventType),
        eventDetails.participantsCount,
        budgetFormatted,
        eventDetails.location || "Not specified",
        !isMonthSelection && !!eventDetails.startDate,
        completionTime
      );
      
      // Route to AI Planner step or success step
      if (isEligibleForAiPlanner(eventDetails.budget, eventDetails.eventType)) {
        setCurrentStep(8);
      } else {
        setCurrentStep(7);
      }
    } catch (error) {
      console.error("Form submission error details:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };
  return {
    currentStep,
    eventDetails,
    contactInfo,
    isSubmitting,
    resetForm,
    handleStepSubmit,
    handleSkipStep,
    handlePreviousStep,
    handleSubmit,
    isOnlyEmailMissing,
    startTracking,
    getElapsedSeconds,
    aiPlannerLoading,
    aiPlannerSessionUrl,
    aiPlannerError
  };
}

// ============= FORM LAYOUT =============
const FormLayout = ({
  title,
  description,
  children,
  image,
  imageCaption,
  split = false
}: FormLayoutProps) => {
  const isLeftImageLayout = split === "left-image";
  return <div className="flex flex-col sm:flex-row h-full max-h-full overflow-hidden">
      {image && isLeftImageLayout && <div className="hidden sm:block sm:w-1/2 lg:w-2/5 relative">
          <img src={image} alt="Event planning" className="w-full h-full object-cover" />
          {imageCaption && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm text-center">
              {imageCaption}
            </div>}
        </div>}

      {image && !isLeftImageLayout && split === false && <div className="hidden sm:block sm:w-2/5 relative">
          <img src={image} alt="Event planning" className="w-full h-full object-cover" />
          {imageCaption && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm text-center">
              {imageCaption}
            </div>}
        </div>}

      <div className={`${isLeftImageLayout ? 'sm:w-1/2 lg:w-3/5' : split ? 'w-full' : image ? 'sm:w-3/5' : 'w-full'} flex flex-col h-full`}>
        {split === true && image ? <div className="bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6 text-center">
            <DialogTitle className="text-2xl md:text-3xl font-normal text-center text-white sf-font">
              {title}
            </DialogTitle>
            {description && <DialogDescription className="text-base pt-2 text-center text-gray-100 sf-font">
                {description}
              </DialogDescription>}
            {image && <div className="mt-3 w-full h-40 overflow-hidden rounded-lg relative">
                <img src={image} alt="Event planning" className="w-full h-full object-cover" />
                {imageCaption && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm text-center">
                    {imageCaption}
                  </div>}
              </div>}
          </div> : <DialogHeader className="p-6 pt-12 pb-4 mx-4 sm:mx-0">
            <DialogTitle className="text-2xl md:text-3xl font-normal text-center text-black sf-font">
              {title}
            </DialogTitle>
            {description && <DialogDescription className="text-base pt-2 text-center text-black sf-font">
              {description.split('**').map((part, index) => 
                index % 2 === 1 ? <strong key={index}>{part}</strong> : part
              )}
            </DialogDescription>}
          </DialogHeader>}
        
        <div className="flex-grow px-6 pb-3 flex flex-col">
          {children}
        </div>
      </div>
    </div>;
};

// ============= FORM STEP CONTAINER =============
const FormStepContainer: React.FC<StepContainerProps> = ({
  title,
  description,
  children,
  image,
  imageCaption,
  split = false,
  currentStep = 1,
  totalSteps = 6,
  hideStepIndicator = false
}) => {
  const progressValue = currentStep / totalSteps * 100;
  return <FormLayout title={title} description={description} image={image} imageCaption={imageCaption} split={split}>
      {!hideStepIndicator && <div className="absolute top-0 left-0 right-0 h-1">
        <Progress value={progressValue} className="h-1 rounded-none" />
      </div>}
      
      <div className="flex flex-col h-full justify-center pt-1 pb-2 md:pb-4 space-y-3 md:space-y-6">
        {children}
      </div>
      
      {!hideStepIndicator && <div className="absolute bottom-0 right-0 bg-white border-t border-l rounded-tl-lg p-2 w-16 h-16 flex flex-col items-center justify-center">
        <div className="relative w-10 h-10">
          <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
            <path className="text-gray-200" d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831" fill="none" stroke="currentColor" strokeWidth="2" />
            <path className="text-primary" d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={`${progressValue}, 100`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-gray-600 font-normal">{currentStep}/{totalSteps}</span>
          </div>
        </div>
      </div>}
    </FormLayout>;
};

// ============= SUCCESS STEP =============
const SuccessStep = ({
  onClose
}: {
  onClose: () => void;
}) => {
  return <div className="flex flex-col items-center py-10 px-4 text-center">
      <div className="rounded-full bg-green-100 p-3 mb-6">
        <CheckCircle className="h-12 w-12 text-green-600" />
      </div>
      
      <h3 className="text-2xl font-normal mb-4">Merci pour votre demande!</h3>
      
      <p className="text-gray-600 mb-8 max-w-md">
        Notre équipe d'experts va se mettre immédiatement au travail pour vous proposer des lieux personnalisés.
        Vous recevrez une sélection sur mesure pour votre événement sous 24 heures.
      </p>
      
      <Button onClick={onClose} className="rounded-xl bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground">
        Fermer
      </Button>
    </div>;
};
const SuccessStepContainer = ({
  onClose,
  image
}: {
  onClose: () => void;
  image?: string;
}) => {
  return <FormStepContainer title="" image={image} onNext={() => {}} initialData={{}} hideStepIndicator>
      <SuccessStep onClose={onClose} />
    </FormStepContainer>;
};

// ============= LOCATION FIELD =============
interface LocationFieldProps {
  control: any;
  label?: string;
  className?: string;
  onChange?: (value: string) => void;
}
const LocationField = ({
  control,
  label = "Saisissez un lieu",
  className,
  onChange
}: LocationFieldProps) => {
  return <FormField control={control} name="location" render={({
    field
  }) => <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MapPinIcon className="h-4 w-4 opacity-50" />
              </div>
              <Input placeholder="Saisissez le lieu souhaité (optionnel) : ville, région ou lieu précis" className="pl-10" {...field} onChange={e => {
          field.onChange(e);
          if (onChange) onChange(e.target.value);
        }} />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>} />;
};

// ============= STEP 1: EVENT TYPE =============
const Step1EventType: React.FC<FormStepProps> = ({
  onNext,
  initialData
}) => {
  const schema = z.object({
    eventType: z.string().min(1, "Veuillez sélectionner un type d'événement")
  });
  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      eventType: initialData.eventType || ""
    }
  });
  const watchedEventType = form.watch("eventType");
  const hasError = !!form.formState.errors.eventType;
  const onSubmit = (data: FormValues) => {
    onNext({
      eventType: data.eventType
    });
  };
  const handleOptionChange = (value: string) => {
    form.setValue("eventType", value);
    if (form.formState.errors.eventType) {
      form.clearErrors("eventType");
    }
  };
  const eventTypes = [{
    value: "corporate_retreat",
    label: "Séminaire Résidentiel"
  }, {
    value: "away_day",
    label: "Journée d'étude"
  }, {
    value: "corporate_party",
    label: "Soirée d'entreprise"
  }, {
    value: "team_building",
    label: "Activité team building"
  }, {
    value: "conference",
    label: "Conférence"
  }, {
    value: "product_launch",
    label: "Lancement de produit"
  }, {
    value: "other",
    label: "Autre"
  }];
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow pt-2 overflow-y-auto pb-6 px-2">
          {hasError && <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                <p className="text-red-800 font-normal text-sm">
                  {form.formState.errors.eventType?.message}
                </p>
              </div>
            </div>}
          
          <FormField control={form.control} name="eventType" render={({
          field
        }) => <div>
                <FormControl>
                  <RadioGroup value={watchedEventType} onValueChange={handleOptionChange} className="grid grid-cols-1 md:grid-cols-2 gap-3 px-1">
                    {eventTypes.map(type => <div key={type.value} className="relative">
                        <RadioGroupItem value={type.value} id={type.value} className="peer sr-only" />
                        <label htmlFor={type.value} className={`flex items-center justify-center px-2 py-3 border ${watchedEventType === type.value ? "border-black bg-gray-50 font-normal" : hasError ? "border-red-300 bg-white hover:border-red-400" : "border-gray-200 bg-white hover:border-cta-green hover:shadow-md hover:shadow-cta-green/20"} rounded-lg cursor-pointer hover:bg-gray-50 transition-all text-sm`}>
                          {type.label}
                        </label>
                      </div>)}
                  </RadioGroup>
                </FormControl>
              </div>} />

        </div>
        
        <div className="flex justify-center mt-auto pt-6 sticky bottom-0 bg-white pb-4">
          <div className="flex space-x-3">
            <Button type="submit" className={`${hasError ? 'bg-red-500 hover:bg-red-600' : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90'} rounded-xl`}>
              Suivant
            </Button>
          </div>
        </div>
      </form>
    </Form>;
};

// ============= STEP 2: DATE SELECTION =============
const Step2DateSelection: React.FC<FormStepProps> = ({
  onNext,
  onPrevious,
  initialData
}) => {
  const schema = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    month: z.string().optional()
  }).refine(data => data.startDate && data.startDate.trim() !== "" || data.month && data.month.trim() !== "", {
    message: "Sélectionner une date ou un mois",
    path: ["startDate"]
  });
  type FormValues = z.infer<typeof schema>;
  const [selectedMonth, setSelectedMonth] = useState<string>(initialData.month || "");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(initialData.startDate && initialData.endDate ? {
    from: new Date(initialData.startDate),
    to: initialData.endDate ? new Date(initialData.endDate) : undefined
  } : undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      startDate: initialData.startDate || "",
      endDate: initialData.endDate || "",
      month: initialData.month || ""
    },
    mode: "onChange"
  });
  const hasError = !!form.formState.errors.startDate;
  const hasValidSelection = useMemo(() => {
    return selectedMonth && selectedMonth.trim() !== "" || dateRange?.from !== undefined;
  }, [selectedMonth, dateRange]);
  useEffect(() => {
    if (selectedMonth) {
      form.setValue("month", selectedMonth);
      form.setValue("startDate", "");
      form.setValue("endDate", "");
    } else if (dateRange?.from) {
      const startDateValue = format(dateRange.from, "yyyy-MM-dd");
      const endDateValue = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
      form.setValue("startDate", startDateValue);
      form.setValue("endDate", endDateValue);
      form.setValue("month", "");
    }
    if (hasValidSelection && form.formState.errors.startDate) {
      form.clearErrors("startDate");
    }
  }, [selectedMonth, dateRange, form, hasValidSelection]);
  const handleCalendarSelect = (range: DateRange | undefined) => {
    if (range?.from) {
      setDateRange(range);
      setSelectedMonth("");
    } else {
      setDateRange(undefined);
    }
  };
  const onSubmit = (data: FormValues) => {
    if (!hasValidSelection) {
      form.setError("startDate", {
        type: "manual",
        message: "Sélectionnez une date ou un mois"
      });
      return;
    }
    const formattedData = {
      ...data,
      startDate: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "",
      endDate: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : ""
    };
    onNext(formattedData);
  };
  const months = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow space-y-6 overflow-y-auto pb-6">
          {hasError && <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                <p className="text-red-800 font-normal text-sm">
                  {form.formState.errors.startDate?.message}
                </p>
              </div>
            </div>}
          
          <FormField control={form.control} name="startDate" render={({
          field
        }) => <div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-normal mb-3">Sélectionnez des dates spécifiques<span className="text-red-500">*</span></h3>
                  
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange && "text-muted-foreground", hasError ? "border-red-300 bg-white hover:border-red-400" : "border-gray-200")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? dateRange.to ? `${format(dateRange.from, "MMM dd, yyyy")} - ${format(dateRange.to, "MMM dd, yyyy")}` : format(dateRange.from, "MMM dd, yyyy") : "Choisir une date ou une période"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                      <Calendar mode="range" selected={dateRange} onSelect={handleCalendarSelect} className="border border-gray-200 rounded-lg bg-white pointer-events-auto" numberOfMonths={1} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-normal mb-3">Ou sélectionnez un mois<span className="text-red-500">*</span></h3>
                  <FormControl>
                    <RadioGroup value={selectedMonth} onValueChange={value => {
                setSelectedMonth(value);
                setDateRange(undefined);
              }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {months.map(month => <div key={month} className="relative">
                          <RadioGroupItem value={month} id={`month-${month}`} className="peer sr-only" />
                          <label htmlFor={`month-${month}`} className={`flex items-center justify-center px-3 py-2 bg-white border ${selectedMonth === month ? "border-black bg-gray-50 font-normal" : hasError ? "border-red-300 bg-white hover:border-red-400" : "border-gray-200 hover:border-cta-green hover:shadow-md hover:shadow-cta-green/20"} rounded-lg cursor-pointer hover:bg-gray-50 transition-all text-sm`}>
                            {month}
                          </label>
                        </div>)}
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>} />
        </div>

        <div className="flex justify-center mt-auto pt-6 sticky bottom-0 bg-white pb-4">
          <div className="flex space-x-3">
            {onPrevious && <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 rounded-xl flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour
              </Button>}
            <Button type="submit" className={`${hasError ? 'bg-red-500 hover:bg-red-600' : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90'} rounded-xl`}>
              Suivant
            </Button>
          </div>
        </div>
      </form>
    </Form>;
};

// ============= STEP 3: LOCATION =============
const Step3Location: React.FC<FormStepProps> = ({
  onNext,
  onPrevious,
  onSkip,
  initialData
}) => {
  const schema = z.object({
    location: z.string().optional()
  });
  type FormValues = z.infer<typeof schema>;
  const [selectedCity, setSelectedCity] = useState<string>(initialData.location || "");
  const isMobile = useIsMobile();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      location: initialData.location || ""
    }
  });
  const handleCitySelection = (cityName: string) => {
    form.setValue("location", cityName);
    setSelectedCity(cityName);
  };
  const onSubmit = (data: FormValues) => {
    onNext(data);
  };
  const popularCities = ["Paris, France", "Normandie, France", "Côte d'Azur, France", "Rhônes-Alpes, France", "Espagne", "Maroc"];
  const displayCities = isMobile ? popularCities.slice(0, 4) : popularCities;
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow space-y-6 overflow-y-auto pb-6">
          {form.formState.errors.location && <p className="text-red-500 text-sm mb-3 ml-2">
              {form.formState.errors.location.message}
            </p>}
          
          <LocationField control={form.control} label="Saisissez un lieu" onChange={value => setSelectedCity(value)} />

          <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-100 mx-2">
            <h3 className="font-normal mb-3">Destinations populaires</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-1">
              {displayCities.map(city => <button key={city} type="button" onClick={() => handleCitySelection(city)} className={`flex items-center justify-center px-3 py-3 bg-white border ${selectedCity === city ? "border-black bg-gray-50" : "border-gray-200 hover:border-cta-green hover:shadow-md hover:shadow-cta-green/20"} rounded-lg cursor-pointer hover:bg-gray-50 transition-all text-sm`}>
                  {city}
                </button>)}
            </div>
          </div>
        </div>

        {onSkip && <div className="flex justify-center pb-4">
            <Button type="button" variant="ghost" onClick={() => onSkip()} className="font-bold text-gray-600 hover:bg-transparent hover:text-gray-900">
              Passer cette étape
            </Button>
          </div>}

        <div className="flex justify-center mt-auto pt-6 sticky bottom-0 bg-white pb-4">
          <div className="flex space-x-3">
            {onPrevious && <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 rounded-xl flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour
              </Button>}
            <Button type="submit" className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 rounded-xl">
              Suivant
            </Button>
          </div>
        </div>
      </form>
    </Form>;
};

// ============= STEP 4: PARTICIPANTS =============
const Step4Participants: React.FC<FormStepProps> = ({
  onNext,
  onPrevious,
  initialData
}) => {
  const schema = z.object({
    participantsCount: z.string().min(1, "Indiquez le nombre de participants")
  });
  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      participantsCount: initialData.participantsCount || ""
    },
    mode: "onChange"
  });
  const hasError = !!form.formState.errors.participantsCount;
  const onSubmit = (data: FormValues) => {
    if (!data.participantsCount || data.participantsCount.trim() === "") {
      form.setError("participantsCount", {
        type: "manual",
        message: "Indiquez le nombre de participants"
      });
      return;
    }
    onNext(data);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue("participantsCount", value);
    if (value && form.formState.errors.participantsCount) {
      form.clearErrors("participantsCount");
    }
  };
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow overflow-y-auto pb-6">
          {hasError && <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                <p className="text-red-800 font-normal text-sm">
                  {form.formState.errors.participantsCount?.message}
                </p>
              </div>
            </div>}
          
          <div className="max-w-[90%] mx-auto">
            <FormLabel className="text-base mb-2 block">Combien de participants ? <span className="text-red-500">*</span></FormLabel>
          
            <FormField control={form.control} name="participantsCount" render={({
            field
          }) => <FormItem>
                  <FormControl>
                    <Input placeholder="ex: 30" className={`text-lg py-6 ${hasError ? 'border-red-300 bg-white hover:border-red-400' : 'border-gray-200'}`} {...field} onChange={handleInputChange} />
                  </FormControl>
                </FormItem>} />
          </div>
        </div>

        <div className="flex justify-center mt-auto pt-6 sticky bottom-0 bg-white pb-4">
          <div className="flex space-x-3">
            {onPrevious && <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 rounded-xl flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour
              </Button>}
            <Button type="submit" className={`${hasError ? 'bg-red-500 hover:bg-red-600' : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90'} rounded-xl`}>
              Suivant
            </Button>
          </div>
        </div>
      </form>
    </Form>;
};

// ============= STEP 5: BUDGET =============
const Step5Budget: React.FC<FormStepProps> = ({
  onNext,
  onPrevious,
  onSkip,
  initialData
}) => {
  // Calculate budget range based on participants count
  const participantsCount = parseInt(initialData.participantsCount || "10");
  const getMinBudgetPerPerson = (eventType: string): number => {
    switch (eventType) {
      case "corporate_retreat": return 150;
      case "away_day": return 75;
      default: return 50;
    }
  };
  const minBudgetPerPerson = getMinBudgetPerPerson(initialData.eventType || "");
  const maxBudgetPerPerson = 1000;
  const minTotalBudget = participantsCount * minBudgetPerPerson;
  const maxTotalBudget = participantsCount * maxBudgetPerPerson;

  // Calculate appropriate step based on budget range
  const budgetRange = maxTotalBudget - minTotalBudget;
  const step = budgetRange <= 5000 ? 100 : budgetRange <= 20000 ? 500 : 1000;
  const formatCurrency = (value: number): string => {
    if (value >= maxTotalBudget) {
      return `${maxTotalBudget.toLocaleString('fr-FR')} €+`;
    }
    return `${value.toLocaleString('fr-FR')} €`;
  };
  const parseBudgetValue = (budgetString: string): number => {
    if (!budgetString) return Math.round((minTotalBudget + maxTotalBudget) / 2); // default middle value
    if (budgetString.includes("+")) return maxTotalBudget;
    const numericValue = parseInt(budgetString.replace(/[^\d]/g, ''), 10);
    return isNaN(numericValue) ? Math.round((minTotalBudget + maxTotalBudget) / 2) : Math.max(minTotalBudget, Math.min(numericValue, maxTotalBudget));
  };
  const schema = z.object({
    budget: z.string().min(1, "Veuillez définir un budget")
  });
  type FormValues = z.infer<typeof schema>;
  const [sliderValue, setSliderValue] = React.useState<number>(parseBudgetValue(initialData.budget || ""));
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      budget: initialData.budget || formatCurrency(Math.round((minTotalBudget + maxTotalBudget) / 2))
    },
    mode: "onChange"
  });
  const hasError = !!form.formState.errors.budget;
  const onSubmit = (data: FormValues) => {
    if (!data.budget || data.budget.trim() === "") {
      form.setError("budget", {
        type: "manual",
        message: "Veuillez définir un budget"
      });
      return;
    }
    onNext(data);
  };
  const handleSliderChange = (value: number[]) => {
    const newValue = value[0];
    setSliderValue(newValue);
    const formattedValue = formatCurrency(newValue);
    form.setValue("budget", formattedValue);
    if (form.formState.errors.budget) {
      form.clearErrors("budget");
    }
  };
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow pt-4 overflow-y-auto pb-6 px-2">
          {hasError && <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                <p className="text-red-800 font-normal text-sm">
                  {form.formState.errors.budget?.message}
                </p>
              </div>
            </div>}
          
          <FormField control={form.control} name="budget" render={({
          field
        }) => <div className="space-y-6">
              <div className="text-center">
                <label className="block text-sm font-normal text-gray-700 mb-2">
                  Budget total<span className="text-red-500">*</span>
                </label>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {formatCurrency(sliderValue)}
                </div>
                {initialData.participantsCount && <div className="text-lg text-gray-600 mb-4">
                    ({Math.round(sliderValue / parseInt(initialData.participantsCount)).toLocaleString('fr-FR')} € par personne)
                  </div>}
              </div>
              
              <FormControl>
                <div className="px-4 space-y-4">
                  <Slider value={[sliderValue]} onValueChange={handleSliderChange} max={maxTotalBudget} min={minTotalBudget} step={step} className="w-full" />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{minTotalBudget.toLocaleString('fr-FR')} €</span>
                    <span>{maxTotalBudget.toLocaleString('fr-FR')} €+</span>
                  </div>
                </div>
              </FormControl>
            </div>} />
        </div>

        <div className="flex justify-center mt-auto pt-6 sticky bottom-0 bg-white pb-4">
          <div className="flex space-x-3">
            {onPrevious && <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 rounded-xl flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour
              </Button>}
            <Button type="submit" className={`${hasError ? 'bg-red-500 hover:bg-red-600' : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90'} rounded-xl`}>
              Suivant
            </Button>
          </div>
        </div>
      </form>
    </Form>;
};

// ============= GENERIC EMAIL DETECTION =============
const isGenericEmail = (email: string): boolean => {
  const localPart = email.split('@')[0]?.toLowerCase();
  if (!localPart) return false;
  const genericPrefixes = [
    "info", "contact", "admin", "hello", "bonjour", "accueil", "reception",
    "direction", "service", "support", "commercial", "communication", "comm",
    "marketing", "rh", "hr", "comptabilite", "compta", "facturation",
    "billing", "sales", "office", "bureau", "secretariat", "general",
    "courrier", "mail", "team", "equipe", "event", "events", "evenement",
    "evenements", "booking", "reservation", "reservations", "projet",
    "projets", "gestion", "management", "partenariat", "partenariats",
    "presse", "media", "medias", "formation", "formations", "recrutement",
    "talent", "talents", "achats", "procurement", "logistique", "supply",
    "technique", "tech", "it", "dsi", "finance", "finances", "juridique",
    "legal", "qualite", "quality", "production", "operations", "ops",
    "noreply", "no-reply", "do-not-reply", "postmaster", "webmaster",
    "abuse", "root", "sav", "helpdesk", "help", "assistance",
    // Role/function words
    "assistant", "assistante", "directeur", "directrice", "responsable",
    "coordinateur", "coordinatrice", "stagiaire", "president", "presidente",
    "gerant", "gerante", "secretaire", "comptable", "tresorier", "tresoriere",
    "delegue", "deleguee", "charge", "chargee", "adjoint", "adjointe",
    "referent", "referente", "animateur", "animatrice", "conseiller",
    "conseillere", "ingenieur", "technicien", "technicienne", "superviseur",
    "manager", "chef", "maire", "notaire", "avocat", "avocate",
    "medecin", "pharmacien", "pharmacienne", "greffier", "greffiere",
    "intendant", "intendante", "econome", "standardiste", "regulateur",
    "test"
  ];

  // Split local part by common separators and check if any segment is generic
  const segments = localPart.split(/[.\-_]/);
  return segments.some(seg => genericPrefixes.includes(seg));
};

// ============= STEP 6: CONTACT =============
const Step6Contact: React.FC<FormStepProps> = ({
  onNext,
  onPrevious,
  onSkip,
  initialData,
  isSubmitting
}) => {
  const [showNameFields, setShowNameFields] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const schema = z.object({
    workEmail: z.string().min(1, "L'email est requis").email("Adresse e-mail incorrect").refine(email => {
      const freeDomains = ["gmail.com", "googlemail.com", "hotmail.com", "hotmail.fr", "outlook.com", "outlook.fr", "live.com", "live.fr", "msn.com", "yahoo.com", "yahoo.fr", "ymail.com", "rocketmail.com", "icloud.com", "me.com", "mac.com", "aol.com", "aol.fr", "laposte.net", "orange.fr", "wanadoo.fr", "sfr.fr", "neuf.fr", "club-internet.fr", "cegetel.net", "numericable.fr", "free.fr", "aliceadsl.fr", "libertysurf.fr", "infonie.fr", "bbox.fr", "tele2.fr", "protonmail.com", "proton.me", "pm.me", "zoho.com", "gmx.fr", "gmx.com", "gmx.net", "mail.com", "yandex.com", "yandex.fr", "inbox.com", "bluewin.ch", "t-online.de", "web.de", "posteo.net", "caramail.com", "netcourrier.com", "keemail.me", "tutanota.com", "ueir.jci"];
      const domain = email.split('@')[1];
      return !freeDomains.includes(domain?.toLowerCase());
    }, "Veuillez utiliser votre email professionnel"),
    phoneNumber: z.string().optional()
  });
  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      workEmail: initialData?.workEmail || "",
      phoneNumber: initialData?.phoneNumber || ""
    },
    mode: "onChange"
  });
  const hasError = !!form.formState.errors.workEmail;
  const onSubmit = (data: FormValues) => {
    if (!data.workEmail || data.workEmail.trim() === "") {
      form.setError("workEmail", {
        type: "manual",
        message: "L'email est requis"
      });
      return;
    }
    onNext({
      workEmail: data.workEmail,
      phoneNumber: data.phoneNumber || "",
      firstName: showNameFields ? firstName : "",
      lastName: showNameFields ? lastName : ""
    });
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue("workEmail", value);
    if (value && form.formState.errors.workEmail) {
      form.clearErrors("workEmail");
    }
    // Detect generic email
    if (value.includes('@') && value.split('@')[1]?.length > 0) {
      setShowNameFields(isGenericEmail(value));
    } else {
      setShowNameFields(false);
    }
  };
  return <>
      {/* Photo + Titre côte à côte */}
      <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-6">
        <img src="/lovable-uploads/6713f751-6c4a-4c22-9584-4ea70230a2a1.png" alt="Alex profile" className="w-14 h-14 md:w-20 md:h-20 rounded-full flex-shrink-0" />
        <div>
          <h2 className="text-xl md:text-3xl font-normal text-black sf-font mb-1">
            Alex s'occupe de votre projet !
          </h2>
          <p className="text-sm md:text-base text-black sf-font font-normal">
            Où souhaitez-vous recevoir vos suggestions personnalisées ?
          </p>
        </div>
      </div>
      
      <div className="flex-grow space-y-2 md:space-y-6 overflow-y-auto pb-1 md:pb-6">
         
        <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2 md:space-y-4 px-2" id="email-form">
                <FormField control={form.control} name="workEmail" render={({
            field
          }) => <FormItem>
                      <FormControl>
                        <Input type="email" placeholder="Entrez votre adresse e-mail professionnelle" className="text-base h-12 email-input" {...field} id="email-input" onChange={handleInputChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="phoneNumber" render={({
            field
          }) => <FormItem>
                      <FormControl>
                        <Input type="tel" placeholder="Votre numéro de téléphone (optionnel)" className="text-base h-12" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">
                        Indiquez votre numéro et votre conseiller dédié vous appellera directement.
                      </p>
                      <FormMessage />
                    </FormItem>} />
                
                {showNameFields && (
                  <div className="space-y-3 animate-fade-in">
                    <p className="text-sm text-muted-foreground italic">
                      Vous utilisez un email générique ? N'hésitez pas à partager votre prénom et nom afin de fluidifier nos échanges
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="text"
                        placeholder="Prénom"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="text-base h-12"
                      />
                      <Input
                        type="text"
                        placeholder="Nom"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="text-base h-12"
                      />
                    </div>
                  </div>
                )}
              </form>
            </Form>
            
            <div className="flex items-start space-x-2 text-sm text-gray-500">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Nous accordons une grande importance à votre vie privée. Vos informations de contact sont uniquement utilisées pour traiter vos demandes et ne seront ni partagées, ni vendues, ni utilisées pour vous envoyer des spams.</span>
            </div>
          </div>

      
      <div className="flex justify-center mt-auto pt-3 md:pt-6 sticky bottom-0 bg-white pb-4">
        <div className="flex space-x-3">
          {onPrevious && <Button type="button" variant="outline" onClick={onPrevious} className="border-gray-300 rounded-xl flex items-center justify-center gap-2" disabled={isSubmitting}>
              <ArrowLeft className="h-4 w-4" />
              Retour
            </Button>}
          <Button type="submit" form="email-form" className={`${hasError ? 'bg-red-500 hover:bg-red-600' : 'bg-cta-green text-cta-green-foreground hover:bg-cta-green/90'} rounded-xl`} disabled={isSubmitting}>
            {isSubmitting ? "Envoi..." : "Envoyer"}
          </Button>
        </div>
      </div>
    </>;
};

// ============= STEP RENDERER =============
interface StepRendererProps {
  currentStep: number;
  eventDetails: any;
  contactInfo: any;
  isSubmitting: boolean;
  handleStepSubmit: (data: any, step: number) => void;
  handleSkipStep: (step: number) => void;
  handlePreviousStep: () => void;
  handleClose: () => void;
  aiPlannerLoading: boolean;
  aiPlannerSessionUrl: string | null;
  aiPlannerError: boolean;
}
const StepRenderer: React.FC<StepRendererProps> = ({
  currentStep,
  eventDetails,
  contactInfo,
  isSubmitting,
  handleStepSubmit,
  handleSkipStep,
  handlePreviousStep,
  handleClose,
  aiPlannerLoading,
  aiPlannerSessionUrl,
  aiPlannerError
}) => {
  const totalSteps = 6;

  const getEventLabel = (eventType?: string): string => {
    const labels: Record<string, string> = {
      corporate_retreat: "séminaire résidentiel",
      away_day: "journée d'étude",
      corporate_party: "soirée d'entreprise",
      team_building: "activité team building",
      conference: "conférence",
      product_launch: "lancement de produit",
    };
    return labels[eventType || ""] || "événement";
  };

  const eventLabel = getEventLabel(eventDetails.eventType);

  const eventTypeImage = "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75";
  const dateSelectionImage = "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75";
  const locationImage = "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/402466422.jpg";
  const participantsImage = "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FoxHh1673372697492.jpg&w=2048&q=75";
  const budgetImage = "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75";
  const contactImage = "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75";
  const successImage = "/lovable-uploads/b2375f6b-6acd-42bf-9e91-6b39fc5503a3.png";
  switch (currentStep) {
    case 1:
      return <FormStepContainer title="Organisons ensemble votre événement idéal" description="Remplissez ce court formulaire et recevez des suggestions de lieux personnalisées sous 24 heures, **gratuitement et sans engagement**" image={eventTypeImage} imageCaption="L'Abbaye de Royaumont, Île-de-France" split="left-image" onNext={data => handleStepSubmit(data, 1)} initialData={eventDetails} showBackButton={false} currentStep={currentStep} totalSteps={totalSteps}>
          <Step1EventType onNext={data => handleStepSubmit(data, 1)} initialData={eventDetails} />
        </FormStepContainer>;
    case 2:
      return <FormStepContainer title={`Quand prévoyez-vous d'organiser votre ${eventLabel} ?`} image={dateSelectionImage} imageCaption="Château d'Ermenonville, Hauts-de-France" onNext={data => handleStepSubmit(data, 2)} onPrevious={handlePreviousStep} initialData={eventDetails} currentStep={currentStep} totalSteps={totalSteps}>
          <Step2DateSelection onNext={data => handleStepSubmit(data, 2)} onPrevious={handlePreviousStep} initialData={eventDetails} />
        </FormStepContainer>;
    case 3:
      return <FormStepContainer title={`Où souhaitez-vous organiser votre ${eventLabel} ?`} image={locationImage} imageCaption="Youza Ecolodge, Île-de-France" onNext={data => handleStepSubmit(data, 3)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(3)} initialData={eventDetails} canSkip={true} currentStep={currentStep} totalSteps={totalSteps}>
          <Step3Location onNext={data => handleStepSubmit(data, 3)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(3)} initialData={eventDetails} />
        </FormStepContainer>;
    case 4:
      return <FormStepContainer title="Combien de participants y aura-t-il ?" description="(Une estimation suffit)" image={participantsImage} imageCaption="Etiolles Country Club, Île-de-France" onNext={data => handleStepSubmit(data, 4)} onPrevious={handlePreviousStep} initialData={eventDetails} currentStep={currentStep} totalSteps={totalSteps}>
          <Step4Participants onNext={data => handleStepSubmit(data, 4)} onPrevious={handlePreviousStep} initialData={eventDetails} />
        </FormStepContainer>;
    case 5:
      return <FormStepContainer title="Quel est votre budget total?" description="(transport non inclus)" image={budgetImage} imageCaption="Le Domaine Rocabella, Provence Alpes Côte d'Azur" onNext={data => handleStepSubmit(data, 5)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(5)} initialData={eventDetails} canSkip={true} currentStep={currentStep} totalSteps={totalSteps}>
          <Step5Budget onNext={data => handleStepSubmit(data, 5)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(5)} initialData={eventDetails} />
        </FormStepContainer>;
    case 6:
      return <FormStepContainer title="" description="" image={contactImage} imageCaption="Hôtel Mercure Chantilly Resort, Hauts-de-France" onNext={data => handleStepSubmit(data, 6)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(6)} initialData={contactInfo} isSubmitting={isSubmitting} canSkip={true} currentStep={currentStep} totalSteps={totalSteps}>
          <Step6Contact onNext={data => handleStepSubmit(data, 6)} onPrevious={handlePreviousStep} onSkip={() => handleSkipStep(6)} initialData={contactInfo} isSubmitting={isSubmitting} />
        </FormStepContainer>;
    case 7:
      return <SuccessStepContainer onClose={handleClose} image={successImage} />;
    case 8:
      return <FormStepContainer title="" image={undefined} onNext={() => {}} initialData={{}} hideStepIndicator>
          <Step8AiSuggestions
            isLoading={aiPlannerLoading}
            sessionUrl={aiPlannerSessionUrl}
            error={aiPlannerError}
            onClose={handleClose}
            contactEmail={contactInfo.workEmail}
            participantsCount={eventDetails.participantsCount}
            budget={eventDetails.budget}
            eventType={eventDetails.eventType}
            location={eventDetails.location}
          />
        </FormStepContainer>;
    default:
      return null;
  }
};

// ============= MAIN MODAL COMPONENT =============
interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFormClose?: (formData: {
    eventDetails: EventDetailsFormData;
    contactInfo: ContactInfoFormData;
    isOnlyEmailMissing: boolean;
  }) => void;
  initialFormData?: {
    eventDetails: EventDetailsFormData;
    contactInfo: ContactInfoFormData;
    startAtEmailStep?: boolean;
  };
  triggerLocation?: string;
  triggerText?: string;
}
const LeadFormModal = ({
  isOpen,
  onClose,
  onFormClose,
  initialFormData,
  triggerLocation = "unknown",
  triggerText = "unknown"
}: LeadFormModalProps) => {
  const leadFormContext = useLeadForm(initialFormData);
  const {
    resetForm,
    isOnlyEmailMissing,
    currentStep,
    eventDetails,
    contactInfo,
    startTracking,
    getElapsedSeconds
  } = leadFormContext;
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  
  // Preload all images instantly
  useEffect(() => {
    const imagesToPreload = [
      "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75",
      "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75",
      "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/402466422.jpg",
      "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FoxHh1673372697492.jpg&w=2048&q=75",
      "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75",
      "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75",
      "/lovable-uploads/b2375f6b-6acd-42bf-9e91-6b39fc5503a3.png",
      "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/16b43907-6361-4531-bcfd-9cf8d7a4f70b.png"
    ];

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Track brief_modal_opened strictly on modal open
  useEffect(() => {
    if (isOpen) {
      posthog.capture('brief_modal_opened');
    }
  }, [isOpen]);

  // Track form opened and start tracking time
  useEffect(() => {
    if (isOpen) {
      startTracking(); // Start tracking form time
      trackFormOpened(triggerLocation, triggerText);
      
      // Track first step viewed
      trackFormStepViewed(1, "Event Type", false);
    }
  }, [isOpen, triggerLocation, triggerText, startTracking]);
  const handleConfirmClose = () => {
    const formData = {
      eventDetails,
      contactInfo,
      isOnlyEmailMissing: isOnlyEmailMissing()
    };
    
    // Track form step abandoned (if not on success step)
    if (currentStep !== 7 && currentStep !== 8) {
      const stepNames = ["Event Type", "Date Selection", "Location", "Participants", "Budget", "Contact Info"];
      
      // Prepare form data and sanitize it
      const formDataCaptured = sanitizeFormData({
        eventType: eventDetails.eventType,
        startDate: eventDetails.startDate,
        endDate: eventDetails.endDate,
        month: eventDetails.month,
        location: eventDetails.location,
        locationType: eventDetails.locationType,
        participantsCount: eventDetails.participantsCount,
        budget: eventDetails.budget,
        workEmail: contactInfo.workEmail, // Will be removed by sanitizeFormData
        phoneNumber: contactInfo.phoneNumber, // Will be removed by sanitizeFormData
      });
      
      trackFormStepAbandoned(
        currentStep,
        stepNames[currentStep - 1] || "Unknown",
        formDataCaptured,
        getElapsedSeconds() // Add time spent
      );
    }
    
    setShowConfirmClose(false);
    resetForm();
    onClose();

    // Notifier le parent avec les données du formulaire
    if (onFormClose) {
      onFormClose(formData);
    }
  };
  const handleCloseAttempt = () => {
    // Si on est à l'étape de succès (étape 7), fermer directement
    if (currentStep === 7 || currentStep === 8) {
      handleConfirmClose();
      return;
    }

    // Si on est sur l'étape 6 (email), montrer la modale de confirmation
    if (currentStep === 6) {
      setShowConfirmClose(true);
      return;
    }

    // Pour toutes les autres étapes, fermer directement sans confirmation
    handleConfirmClose();
  };
  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };
  useEffect(() => {
    if (isOpen) {
      const dialogOverlay = document.querySelector('[data-radix-dialog-overlay]');
      const dialogContent = document.querySelector('[data-radix-dialog-content]');
      if (dialogOverlay) {
        (dialogOverlay as HTMLElement).style.zIndex = '99999';
      }
      if (dialogContent) {
        (dialogContent as HTMLElement).style.zIndex = '100000';
      }
      document.body.style.position = 'relative';
      const styleTag = document.createElement('style');
      styleTag.id = 'lead-form-modal-styles';
      styleTag.innerHTML = `
        [data-radix-dialog-overlay], [data-radix-dialog-content] { 
          z-index: 99999 !important;
        }
        .axeptio_widget, .axeptio_overlay, #axeptio_overlay, 
        .cookieConsentBanner, #cookieConsentBanner, 
        [data-testid="cookie-banner"], .cookie-popup, 
        .cookie-consent, .cookie-notice, 
        .cc-window, .cc-banner, #onetrust-banner-sdk, 
        #onetrust-consent-sdk, #cookieNotice, #cookie-notice {
          z-index: 10 !important;
        }
      `;
      document.head.appendChild(styleTag);
    } else {
      document.body.style.position = '';
      const styleTag = document.getElementById('lead-form-modal-styles');
      if (styleTag) {
        styleTag.remove();
      }
    }
    return () => {
      document.body.style.position = '';
      const styleTag = document.getElementById('lead-form-modal-styles');
      if (styleTag) {
        styleTag.remove();
      }
    };
  }, [isOpen]);
  return <>
      <Dialog open={isOpen} onOpenChange={open => !open && handleCloseAttempt()} modal={true}>
        <DialogContent className="sm:max-w-md md:max-w-4xl lg:max-w-5xl overflow-hidden p-0 w-full h-[650px] max-h-[650px]" onPointerDownOutside={e => {
        e.preventDefault();
      }} onEscapeKeyDown={e => {
        handleCloseAttempt();
      }} aria-describedby="lead-form-description" style={{
        zIndex: 100000
      }}>
          <div id="lead-form-description" className="sr-only">
            Lead generation form to request information about corporate events and retreats
          </div>
          <DialogClose onClick={handleCloseAttempt} className="absolute right-4 top-4 p-2 rounded-full bg-white/90 hover:bg-white text-gray-700 z-50" aria-label="Close form">
            <X className="h-5 w-5" />
          </DialogClose>
          <StepRenderer {...leadFormContext} handleClose={handleConfirmClose} />
        </DialogContent>
      </Dialog>

      <ConfirmCloseModal isOpen={showConfirmClose} onClose={handleCancelClose} onConfirm={handleConfirmClose} />
    </>;
};
export default LeadFormModal;
export { LeadFormModal };